import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  BarChart3, Users, Star, TrendingUp, Globe, CalendarClock,
} from "lucide-react";

/**
 * CohortInsightsPanel — Tier 2 counsellor tool.
 *
 * Hits `/counsellors/cohorts/{id}/insights` and renders a summary
 * strip with student count, avg fit score, entry-realism mix and
 * deadline density. Sits above the class comparison table on the
 * cohort detail view.
 */
export default function CohortInsightsPanel({ cohortId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!cohortId) return;
    let cancelled = false;
    api
      .get(`/counsellors/cohorts/${cohortId}/insights`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [cohortId]);

  if (err) return null;
  if (!data) {
    return (
      <div
        data-testid="cohort-insights-loading"
        className="mt-6 h-24 rounded-lg border border-stone-200 bg-stone-50/50 animate-pulse"
      />
    );
  }

  const realismTotal = Object.values(data.realism_mix || {}).reduce((a, b) => a + b, 0) || 1;
  const dl = data.deadline_buckets || {};

  return (
    <section
      className="mt-6 border border-stone-200 rounded-lg bg-white p-5"
      data-testid="cohort-insights"
    >
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-[#4C1D95]" />
        <span className="label-uppercase text-stone-600">
          Cohort insights
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Stat
          icon={Users}
          label="Students"
          value={data.student_count}
          testid="insights-student-count"
        />
        <Stat
          icon={Star}
          label="Starred picks"
          value={data.starred_count}
          testid="insights-starred-count"
        />
        <Stat
          icon={TrendingUp}
          label="Avg fit score"
          value={data.avg_match_score != null ? `${data.avg_match_score}%` : "—"}
          testid="insights-avg-match"
        />
        <Stat
          icon={TrendingUp}
          label="Avg picks / student"
          value={data.avg_picks_per_student ?? "—"}
          testid="insights-avg-picks"
        />
        <Stat
          icon={Globe}
          label="Top country"
          value={data.top_countries?.[0]?.country || "—"}
          hint={data.top_countries?.[0]?.n ? `${data.top_countries[0].n} picks` : ""}
          testid="insights-top-country"
        />
        <Stat
          icon={TrendingUp}
          label="Median LEO 5y"
          value={
            data.salary_median_gbp
              ? `£${Math.round(data.salary_median_gbp / 1000)}k`
              : "—"
          }
          testid="insights-salary-median"
        />
      </div>

      {/* Entry-realism spread */}
      {realismTotal > 1 && (
        <div className="mt-5">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-1.5">
            Realism spread ({realismTotal} picks)
          </div>
          <div
            className="flex h-3 rounded-full overflow-hidden border border-stone-200"
            data-testid="insights-realism-bar"
          >
            {Object.entries(data.realism_mix || {}).map(([k, n]) => {
              const pct = (n / realismTotal) * 100;
              if (pct < 0.5) return null;
              const cls =
                k === "safe" ? "bg-emerald-500" :
                k === "match" ? "bg-amber-500" :
                k === "reach" ? "bg-rose-500" :
                "bg-stone-300";
              return (
                <div
                  key={k}
                  className={cls}
                  style={{ width: `${pct}%` }}
                  title={`${k}: ${n} (${pct.toFixed(0)}%)`}
                />
              );
            })}
          </div>
          <div className="mt-1.5 flex gap-3 text-[11px] text-stone-600">
            {Object.entries(data.realism_mix || {}).map(([k, n]) => (
              <span key={k} data-testid={`insights-realism-${k}`}>
                {k}: <span className="font-semibold text-stone-900">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Deadline density buckets */}
      <div className="mt-5">
        <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2 flex items-center gap-1.5">
          <CalendarClock className="w-3 h-3" /> Upcoming deadlines
        </div>
        <div className="grid grid-cols-4 gap-2">
          <DeadlineBucket label="Next 30d" n={dl.next_30 ?? 0} intense testid="insights-dl-30" />
          <DeadlineBucket label="30–60d"  n={dl.next_60 ?? 0} testid="insights-dl-60" />
          <DeadlineBucket label="60–90d"  n={dl.next_90 ?? 0} testid="insights-dl-90" />
          <DeadlineBucket label="90d+"    n={dl.over_90 ?? 0} testid="insights-dl-later" />
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, hint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] font-underground-sc tracking-widest text-stone-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 font-editorial text-2xl leading-none tabular-nums text-stone-900">
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[10px] text-stone-500">{hint}</div>}
    </div>
  );
}

function DeadlineBucket({ label, n, intense, testid }) {
  return (
    <div
      className={`px-3 py-2 rounded border ${
        intense && n > 0
          ? "border-rose-300 bg-rose-50"
          : "border-stone-200 bg-stone-50"
      }`}
      data-testid={testid}
    >
      <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
        {label}
      </div>
      <div className={`mt-0.5 font-semibold text-lg tabular-nums ${
        intense && n > 0 ? "text-rose-700" : "text-stone-900"
      }`}>
        {n}
      </div>
    </div>
  );
}
